/**
 * Bulk Assign / Change due / Change queue / Block for selected list rows (#22).
 *
 * There is no bulk API: the first dialog runs through `ActionForm` on the lead
 * task_ref, then the same fields are applied to the rest via `useStudioAction`.
 *
 * **One refusal is not the end of the batch.** The loop used to be a bare `await`
 * inside one `try`: the first task the server said no to threw, the rest were never
 * attempted, the selection cleared anyway and the line read "A task cannot block from
 * open." — which names neither the task nor how much of the batch had happened.
 * Worse, the throw travelled out of an unawaited promise and landed in the console as
 * an uncaught error. So every task is now attempted on its own, every refusal is
 * caught and counted, the sentence names the tasks that refused and **only those
 * rows stay selected**, which is what makes "fix them and press it again" a move.
 *
 * **The sentence is the page's, not this bar's.** It has to be: a batch every row
 * accepted leaves nothing selected, and this component draws nothing when nothing is
 * selected — so a count held here was written by a component on its way out and was
 * never read. It goes to `taskNotice.tsx`, which is docked beside this bar, is the
 * one line a cell edit and a row action already write to, and is replaced by whatever
 * happens next rather than kept until something happens to bring it back.
 *
 * **Two of the four ask first.** Everything here is one dialog and one press over
 * dozens of rows, and two of those presses are not edits anybody can eyeball
 * afterwards: taking the date off a batch, and handing a batch to an agent. Both
 * stop at a confirm that says what is about to happen to how many.
 */

import { TaskSchema, taskMachine, type TaskEvent, type TaskView } from '@fareast/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { z } from 'zod'

import { afterLanded, listenForLanded, useLandedWrites } from '../../../api/hooks.js'
import { isNetworkError, messageOf } from '../../../api/problem.js'
import { useStudioAction } from '../../../api/studio.js'
import { Button } from '../../../components/portal/Button.js'
import { Row } from '../../../components/portal/Layout.js'
import { ConfirmDialog } from '../../../components/portal/Overlay.js'
import { Small, Muted } from '../../../components/portal/Text.js'
import { ActionDialog } from '../../../components/studio/ActionDialog.js'
import { confirmUnassign } from '../../../components/studio/ActionField.js'
import { ownMoveLanded } from '../../../components/studio/ActionForm.js'
import {
  STATUS_WORD,
  editWindowOf,
  heldAs,
  isAboutTask,
  isNewsSince,
  moveWindowOf,
  overtaken,
} from '../../../components/studio/taskWords.js'
import { useFormat } from '../../../format/useFormat.js'
import { useTaskNotice } from './taskNotice.js'

const TaskOut = z.object({ task: TaskSchema })

export type BulkBarProps = {
  /**
   * The selected rows themselves, not their references.
   *
   * A refusal is worth a sentence a reader can act on — "TSK-0107 is open and cannot
   * be blocked" — and the status that explains it is on the row the list already
   * has. Asking the server for it again to write the sentence would be a read to
   * say something this component was handed.
   */
  readonly tasks: readonly TaskView[]
  /**
   * How many tasks the list's filters match, loaded or not (`tasks_summary`), for
   * "3 selected of 126": selection reaches the loaded rows only (Roberto, 21 Sep), and
   * the line says how many more the filters hold. Left out, the line is the count alone.
   */
  readonly matching?: number | undefined
  readonly onClear: () => void
  /** What is left selected when a batch ends: the rows that refused, and nothing else. */
  readonly onFinished: (failed: readonly string[]) => void
}

/** One bulk move: what to call it afterwards, and what it does to one task. */
type Bulk = {
  /** The count line's verb — "Blocked 1 of 2." */
  readonly counted: string
  /** The same verb inside a sentence — "cannot be blocked". */
  readonly past: string
  /** The move the machine has to allow, when this is a move at all. */
  readonly event: TaskEvent | null
  /**
   * Whether a row it was not done to lost a **value** or a **thing to do** — the two
   * end differently when somebody else got there first (`taskWords.ts` `overtaken`).
   */
  readonly made: 'edit' | 'move'
  /**
   * The same change on one more row, **made from what that row says** — its holder
   * and state for a hand-over or a move, its own value for an edit. A batch used to
   * say nothing of what it was made from, on the reasoning that "block these six" is a
   * decision about the six whoever holds each; but the six were chosen from rows up to
   * a minute old, and a task a colleague had claimed in that minute was taken off them
   * by a batch with nothing said to either. The row that changed is refused, named in
   * the page's line and left selected, which is the bar's own way of asking again.
   */
  readonly apply: (task: TaskView) => Promise<unknown>
}

export function BulkBar({ tasks, matching, onClear, onFinished }: BulkBarProps) {
  const format = useFormat()
  const assign = useStudioAction('tasks_assign')
  const update = useStudioAction('tasks_update')
  const block = useStudioAction('tasks_block')
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  /*
   * What the batch did is said in the **page's** line, not in one of the bar's own.
   *
   * The bar is drawn from the selection, and a batch that every row accepted empties
   * that selection — so a sentence held here was rendered by a component that had
   * just been given nothing to draw, and "Saved 3 of 3." was never once seen. Worse,
   * the state outlived the frame: ticking new rows brought the previous batch's
   * sentence back with them. `taskNotice.tsx` is the one line for exactly this, it
   * is docked beside this bar, and it survives the bar going away.
   */
  const notice = useTaskNotice()
  /** A batch that has been asked about and is waiting for the answer. */
  const [asking, setAsking] = useState<{
    readonly title: string
    readonly says: string
    readonly confirmLabel: string
    readonly go: () => Promise<void>
  } | null>(null)

  /*
   * **The selected rows as they were when a window was opened** — and the batch is run
   * from those.
   *
   * A batch says of each row what that row said (`apply`), and "said" has to mean what
   * the reader was looking at when they opened the window: the page behind a window is
   * not looked at, and a read that lands there — one already on its way, or the re-read
   * of a save the reader had just made in a cell — can bring a colleague's change with
   * it. Taken from the page at the press, that change became the row's own condition
   * and passed: the very thing a single task's window pins its condition against
   * (`ActionForm` `useOpenedFrom`).
   *
   * Two copies, for two readers. `seen` is what the bar **draws** while a window is
   * open: the same rows in the same order, so the window stays about one task — the
   * first row is its own, and a list sorted again under it would hand it another.
   * (The tasks page hands it rows that are still under a window already — `api/hooks.ts`
   * `useStill` — but the bar is handed its rows and does not know who by.)
   * `said` is what the batch is **made from**, and it outlives the window, because the
   * batch runs after the window has shut. **Each window has its own**, and a batch
   * keeps the one it came from (`runBulk`): held in one place for the whole bar, it was
   * replaced by the next window to open, and a batch still running — forty rows take a
   * while — went on from the page as it was *then*, with whatever a colleague had
   * changed since its own window opened passing as its condition.
   *
   * **A write of the reader's own amends what its row said, field by field, from that
   * write's own answer** — the same rule a window has, by the same function
   * (`ActionForm` `ownMoveLanded`, through `afterOwnWrite`). The click that leaves a date cell can be the very click that
   * opens "Change due dates": the save was on its way as the window opened, and the
   * row it was taken with still showed the old date. And it can still be on its way
   * at the press, or made while a long batch is running, so the batch asks for each
   * row **when it comes to send it** (`runBulk`) rather than at the press.
   */
  const [seen, setSeen] = useState<readonly TaskView[] | null>(null)
  const said = useRef<Map<string, Said>>(new Map())
  const watch = (open: boolean): void => {
    if (open) said.current = new Map(tasks.map((row) => [row.ref, saidBy(row)]))
    setSeen(open ? tasks : null)
  }
  /* The window's rows, until its batch begins — which then hears for itself (`runBulk`). */
  useLandedWrites((sent, answer) => {
    amendRows(said.current, sent, answer)
  })

  const shown = seen ?? tasks
  if (shown.length === 0) return null
  const [lead, ...rest] = shown
  if (lead === undefined) return null
  const n = String(shown.length)
  /*
   * "Hand 1 tasks to somebody" — the bar counts one as often as it counts six, and
   * the dialogs it opens carry the count in their own titles. A ternary at the call
   * site is the house style here (`components/client/InvoiceTable.tsx`), and three
   * strings do not earn a helper.
   */
  const word = shown.length === 1 ? 'task' : 'tasks'
  const others = String(rest.length)
  const otherWord = rest.length === 1 ? 'task' : 'tasks'

  /**
   * The rest of the batch, one at a time, and what to say about it.
   *
   * The lead is already done — the dialog's own submit did it, and its own refusal
   * appeared in the dialog — so the count starts at one.
   */
  const runBulk = async (bulk: Bulk, rows: Map<string, Said>): Promise<void> => {
    setBusy(true)
    const failed: string[] = []
    const lines: string[] = []
    let done = 1
    /*
     * **A batch hears what lands for as long as it runs, by itself.** The bar's own
     * listener is the bar's: it follows whichever window is open now, and it goes when
     * the bar does — the page left with forty rows still to send — while the batch
     * goes on. Its rows are amended here, by the same rule, until the last is sent.
     */
    const deaf = listenForLanded(queryClient, (sent, answer) => {
      amendRows(rows, sent, answer)
    })
    try {
      for (const [at, drawn] of rest.entries()) {
        /*
         * What the row said, as it stands **now**: a write of the reader's own on this
         * row that is still on its way is waited for, and what it leaves is what the
         * row is sent from. Read at the press instead, a row whose save had not landed
         * yet was refused for the reader's own save, as somebody else's. Read and sent
         * in one turn (`afterLanded`), so that something else let go by the same
         * landing — a date typed in that row — is found on its way, or finds this.
         */
        let task = drawn
        try {
          await afterLanded(
            queryClient,
            (sent) => isAboutTask(sent, drawn.ref),
            () => {
              task = rows.get(drawn.ref)?.row ?? drawn
              return bulk.apply(task)
            },
          )
          done += 1
        } catch (problem) {
          failed.push(task.ref)
          lines.push(refusalOf(task, bulk, problem))
          /*
           * **A request that did not arrive, or was not answered, ends the batch.** It
           * is about the connection and not about that row, and each row after it
           * would say it again: forty rows, forty waits as long as a write is given,
           * with the bar saying "working" for all of them. The rest are not sent, are
           * said not to have been, and stay selected.
           */
          if (isNetworkError(problem)) {
            const unsent = rest.slice(at + 1)
            failed.push(...unsent.map((one) => one.ref))
            if (unsent.length > 0) {
              lines.push(
                `The other ${String(unsent.length)} ${unsent.length === 1 ? 'was' : 'were'} not sent.`,
              )
            }
            break
          }
        }
      }
    } finally {
      deaf()
    }
    setBusy(false)
    const text = [`${bulk.counted} ${String(done)} of ${n}.`, ...lines].join(' ')
    /* A refusal is an alert; a clean batch is a statement. Neither carries an Undo:
       putting a bulk move back is a bulk move, and is the bar's to run again. */
    if (failed.length > 0) notice.fail(text)
    else notice.report({ label: text })
    onFinished(failed)
  }

  /*
   * Each of the four takes `said.current` as it is called, which is as its own window
   * shuts and before any other can open: that map is the batch's from then on. A
   * window cannot be shut while its write is on its way (`ActionDialog` `inFlight`),
   * which is what makes that so — shut by hand before the answer, a second window was
   * opened over it and the batch was made from the second one's rows.
   */
  const afterAssign = (output: unknown): void => {
    const rows = said.current
    const { task } = TaskOut.parse(output)
    // Taking everybody off says so: "Assigned 2 of 2" read as if somebody now had them.
    const unassigned = task.assignee_kind === 'none'
    const bulk: Bulk = {
      counted: unassigned ? 'Unassigned' : 'Assigned',
      past: unassigned ? 'unassigned' : 'assigned',
      event: null,
      made: 'move',
      apply: (row) =>
        assign.mutateAsync({
          task_ref: row.ref,
          assignee_kind: task.assignee_kind,
          assignee_id: task.assignee_id,
          if_was: heldAs(row),
        }),
    }
    if (task.assignee_kind === 'agent' && rest.length > 0) {
      setAsking({
        title: `Hand ${others} more ${otherWord} to an agent?`,
        says: `${lead.ref} has gone to ${task.assignee_name ?? 'an agent'}. The other ${others} ${otherWord} would go to the same agent, which puts them in its queue to pick up.`,
        confirmLabel: `Hand over ${others}`,
        go: () => runBulk(bulk, rows),
      })
      return
    }
    void runBulk(bulk, rows)
  }

  const afterDue = (output: unknown): void => {
    const rows = said.current
    const { task } = TaskOut.parse(output)
    const bulk: Bulk = {
      counted: 'Saved',
      past: 'rescheduled',
      event: null,
      made: 'edit',
      apply: (row) =>
        update.mutateAsync({
          task_ref: row.ref,
          due_at: task.due_at,
          if_was: { due_at: row.due_at },
        }),
    }
    if (task.due_at === null && rest.length > 0) {
      setAsking({
        title: `Take the date off ${others} more ${otherWord}?`,
        says: `${lead.ref} now has no due date. The other ${others} ${otherWord} would lose theirs too, and the dates they have now are not kept anywhere.`,
        confirmLabel: `Clear ${others}`,
        go: () => runBulk(bulk, rows),
      })
      return
    }
    void runBulk(bulk, rows)
  }

  const afterQueue = (output: unknown): void => {
    const { task } = TaskOut.parse(output)
    void runBulk(
      {
        counted: 'Moved',
        past: 'moved',
        event: null,
        made: 'edit',
        apply: (row) =>
          update.mutateAsync({
            task_ref: row.ref,
            queue: task.queue,
            if_was: { queue: row.queue },
          }),
      },
      said.current,
    )
  }

  const afterBlock = (output: unknown): void => {
    const { task } = TaskOut.parse(output)
    const waiting_on = task.waiting_on
    const reason = task.blocked_reason
    if (waiting_on === null || reason === null) return
    void runBulk(
      {
        counted: 'Blocked',
        past: 'blocked',
        event: 'block',
        made: 'move',
        apply: (row) =>
          block.mutateAsync({ task_ref: row.ref, waiting_on, reason, if_was: heldAs(row) }),
      },
      said.current,
    )
  }

  return (
    <div className="tbulk" role="region" aria-label="Bulk actions">
      <Row>
        <Small>
          <Muted>
            {n} selected
            {matching === undefined ? null : ` of ${format.number(matching)}`}
            {busy ? ' — working…' : null}
          </Muted>
        </Small>
        <ActionDialog
          action="tasks_assign"
          label="Assign selected"
          title={`Hand ${n} ${word} to somebody`}
          submitLabel={`Assign ${n}`}
          size="sm"
          {...moveWindowOf(lead)}
          insist
          confirm={confirmUnassign(shown)}
          onOpenChange={watch}
          onDone={afterAssign}
        />
        <ActionDialog
          action="tasks_update"
          label="Change due dates"
          title={`Set one due date on ${n} ${word}`}
          submitLabel={`Save on ${n} ${word}`}
          size="sm"
          {...editWindowOf(lead, 'due_at')}
          insist
          only={['due_at']}
          required={['due_at']}
          onOpenChange={watch}
          onDone={afterDue}
        >
          {/*
            The field's own hint is the single-task one — "Leave it blank and it has
            no date" — and this dialog does not allow that: `required` refuses an
            empty box, because "clear the date on 49 tasks" is not a thing to do by
            leaving a field alone. The line says so where the hint is read.
          */}
          <Small>
            <Muted>
              One date, on all {n} {word}. A date is needed here — the blank box that takes a date
              off a task is the one in the row.
            </Muted>
          </Small>
        </ActionDialog>
        <ActionDialog
          action="tasks_update"
          label="Change queue"
          title={`Move ${n} ${word} to one queue`}
          submitLabel={`Save on ${n} ${word}`}
          size="sm"
          {...editWindowOf(lead, 'queue')}
          insist
          only={['queue']}
          required={['queue']}
          onOpenChange={watch}
          onDone={afterQueue}
        />
        <ActionDialog
          action="tasks_block"
          label="Block selected"
          title={`Block ${n} ${word}`}
          submitLabel={`Block ${n}`}
          size="sm"
          {...moveWindowOf(lead)}
          onOpenChange={watch}
          onDone={afterBlock}
        />
        <Button size="sm" disabled={busy} onClick={onClear}>
          Clear selection
        </Button>
      </Row>
      {asking === null ? null : (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setAsking(null)
          }}
          title={asking.title}
          confirmLabel={asking.confirmLabel}
          busy={busy}
          onConfirm={() => {
            const go = asking.go
            setAsking(null)
            void go()
          }}
        >
          <Small>
            <Muted>{asking.says}</Muted>
          </Small>
        </ConfirmDialog>
      )}
    </div>
  )
}

/**
 * Why one task refused, in words that name it.
 *
 * The server's own sentence is about a *transition* — "A task cannot block from
 * open." — which is true and says nothing about which of the forty-nine rows it is.
 * Where the machine can answer the question locally, the row says it in its own
 * terms; where it cannot, the server's sentence is kept and the reference is put in
 * front of it, because a wrong guess at a reason is worse than a borrowed one.
 */
function refusalOf(task: TaskView, bulk: Bulk, problem: unknown): string {
  /*
   * A request that did not arrive, or was not answered, is about the connection and
   * says nothing of the row: asked after the machine, a row the move was never legal
   * on read "is open and cannot be blocked. The other 3 were not sent." — a batch
   * stopped by the connection, and not one word about it.
   */
  if (isNetworkError(problem)) return `${task.ref}: ${messageOf(problem)}`
  /*
   * Somebody else changed it first: the page's sentence, which names the task itself.
   * Asked **before** the machine, whose answer is about the row as it was drawn — and
   * a row somebody has moved since is exactly a row that is no longer that: "is open
   * and cannot be blocked", beside a task that reads In progress.
   */
  const theirs = overtaken(task.ref, problem, bulk.made)
  if (theirs !== null) return theirs
  if (bulk.event !== null && !taskMachine.can(task.status, bulk.event)) {
    return `${task.ref} is ${STATUS_WORD[task.status].toLowerCase()} and cannot be ${bulk.past}.`
  }
  return `${task.ref}: ${messageOf(problem)}`
}

/** Every row of one window, amended by a write of the reader's own that has just landed. */
function amendRows(rows: Map<string, Said>, sent: unknown, answer: unknown): void {
  for (const [ref, one] of rows) rows.set(ref, afterOwnWrite(one, sent, answer))
}

/** The three things a batch can be made from, each as a row says it. */
const CONDITIONS = {
  held: (row: TaskView) => heldAs(row),
  due_at: (row: TaskView) => ({ due_at: row.due_at }),
  queue: (row: TaskView) => ({ queue: row.queue }),
} as const

type Condition = keyof typeof CONDITIONS

/**
 * What one selected row said, and **when each of the three was last heard**.
 *
 * A stamp for each and not one for the row. Two writes of the reader's own on one row
 * land in either order — a date typed in the Due cell and left by pressing Claim is
 * two, and a write lands when its re-reads are back, not when it was made. One stamp
 * for the row let the claim's answer, which says nothing of the date, make the date's
 * own answer "not news" where it landed second, and the batch was then refused on that
 * row for a date the reader had set.
 */
type Said = {
  readonly row: TaskView
  readonly at: Readonly<Record<Condition, string>>
}

function saidBy(row: TaskView): Said {
  return { row, at: { held: row.updated_at, due_at: row.updated_at, queue: row.updated_at } }
}

/**
 * What a row said, after a write of the reader's own has landed: each of the three
 * taken from that write's answer where the write speaks for it and is news to it
 * (`ActionForm` `ownMoveLanded`, `taskWords.ts` `isNewsSince`), and left alone where
 * it does not.
 */
function afterOwnWrite(said: Said, sent: unknown, answer: unknown): Said {
  const about = { task_ref: said.row.ref }
  let next = said
  for (const name of Object.keys(CONDITIONS) as Condition[]) {
    const landed = ownMoveLanded(about, CONDITIONS[name](said.row), sent, answer)
    if (landed === null || !isNewsSince(said.at[name], landed.stamp)) continue
    next = {
      // The names are the condition's own, which are a task's: the answer's values for them.
      row: { ...next.row, ...(landed.from as Partial<TaskView>) },
      at: { ...next.at, [name]: landed.stamp ?? said.at[name] },
    }
  }
  return next
}
